"use strict";
/**
 * Billing dates the user acts on (BEN-141 FP-147, Paul 2026-10-07: "billing six first").
 *
 * Stripe sends instants (`trial_end`, `current_period_end`). Both clients formatted them
 * with `timeZone: "UTC"`, so a US user saw a period ending 00:00–07:00 UTC a day LATE,
 * and someone east of UTC saw a last-hour end a day EARLY: "renews on" and "free until"
 * wrong by a day on the screen where a person decides whether to pay. The trial
 * countdown rounded the remaining DURATION up (`ceil(ms / 24h)`), so a trial ending this
 * evening read "1 day left", and "Ends today" appeared only after it had ended.
 *
 * Here: format in the device's zone, count calendar days between local dates, and say
 * "Ends today" only when the local dates match and the instant is still ahead.
 * `timeZone` exists so tests can pin a zone; callers omit it (the device's zone).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.TRIAL_ENDED_COPY = exports.TRIAL_ENDS_TOMORROW_COPY = exports.TRIAL_ENDS_TODAY_COPY = void 0;
exports.billingDateLabel = billingDateLabel;
exports.billingDaysUntil = billingDaysUntil;
exports.trialCountdownCopy = trialCountdownCopy;
exports.renewalAfterTrial = renewalAfterTrial;
exports.trialRenewalCopy = trialRenewalCopy;
exports.trialEndedCopy = trialEndedCopy;
exports.TRIAL_ENDS_TODAY_COPY = "Ends today";
exports.TRIAL_ENDS_TOMORROW_COPY = "Ends tomorrow";
exports.TRIAL_ENDED_COPY = "Trial ended";
/** "Oct 7, 2026": the instant's date where the user is. */
function billingDateLabel(instant, timeZone) {
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone,
    }).format(new Date(instant));
}
/** [year, month, day] of `d` in `timeZone` (default: the device's). */
function zonedYmd(d, timeZone) {
    // en-US numeric parts are always "MM/DD/YYYY"; formatToParts is avoided for Hermes.
    const s = new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone,
    }).format(d);
    const [m, day, y] = s.split("/").map(Number);
    return [y, m, day];
}
/** Calendar days from today's local date to the instant's local date (0 = same day). */
function billingDaysUntil(instant, now = new Date(), timeZone) {
    const [y1, m1, d1] = zonedYmd(new Date(instant), timeZone);
    const [y0, m0, d0] = zonedYmd(now, timeZone);
    // Both are calendar dates, so a UTC-day difference is exact across DST.
    return Math.round((Date.UTC(y1, m1 - 1, d1) - Date.UTC(y0, m0 - 1, d0)) / 86400000);
}
/** "Ends today" / "Ends tomorrow" / "N days left" (from 2) / "Trial ended". */
function trialCountdownCopy(trialEnd, now = new Date(), timeZone) {
    if (new Date(trialEnd).getTime() <= now.getTime())
        return exports.TRIAL_ENDED_COPY;
    const days = billingDaysUntil(trialEnd, now, timeZone);
    if (days <= 0)
        return exports.TRIAL_ENDS_TODAY_COPY;
    // Not "1 day left": on the last evening that reads as "I have tomorrow" (web's review).
    if (days === 1)
        return exports.TRIAL_ENDS_TOMORROW_COPY;
    return `${days} days left`;
}
function addInterval(instant, plan) {
    const months = plan === "yearly" ? 12 : 1;
    const y = instant.getUTCFullYear();
    const m = instant.getUTCMonth() + months;
    const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    return new Date(Date.UTC(y, m, Math.min(instant.getUTCDate(), lastDay), instant.getUTCHours(), instant.getUTCMinutes(), instant.getUTCSeconds()));
}
/** The first renewal after a trial: `trialEnd` plus one billing interval. Null without a plan. */
function renewalAfterTrial(trialEnd, plan) {
    const end = new Date(trialEnd);
    if (!plan || Number.isNaN(end.getTime()))
        return null;
    return addInterval(end, plan).toISOString();
}
/**
 * "Your first payment is on Oct 11, and the plan renews on Nov 11." The line under a
 * trial's end date. Without a plan it names the first payment only.
 */
function trialRenewalCopy(trialEnd, plan, timeZone) {
    const first = billingDateLabel(trialEnd, timeZone);
    const renewal = renewalAfterTrial(trialEnd, plan);
    return renewal
        ? `Your first payment is on ${first}, and the plan renews on ${billingDateLabel(renewal, timeZone)}.`
        : `Your first payment is on ${first}.`;
}
/**
 * "Free trial ended Oct 6." for a paid subscription in its first period after the trial,
 * so the renewal date reads against the trial date it follows. Null from the second
 * period on (a year-old trial date is noise), or when the dates do not line up.
 */
function trialEndedCopy(trialEnd, currentPeriodEnd, plan, now = new Date(), timeZone) {
    if (!trialEnd || !currentPeriodEnd)
        return null;
    const end = new Date(trialEnd);
    if (Number.isNaN(end.getTime()) || end.getTime() > now.getTime())
        return null;
    const renewal = renewalAfterTrial(trialEnd, plan);
    if (!renewal)
        return null;
    // Same UTC day: Stripe anchors the period on the trial end to the second, but a day of
    // slack keeps a plan switch's re-anchoring from flickering the line on and off.
    const gap = Math.abs(new Date(renewal).getTime() - new Date(currentPeriodEnd).getTime());
    if (gap > 86400000)
        return null;
    return `Free trial ended ${billingDateLabel(trialEnd, timeZone)}.`;
}
//# sourceMappingURL=billingDates.js.map